import { signedFetch } from '../../super-picker/services/api';

const PREFIX = '/api/communication';

export type EmailTemplateStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';

export interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  htmlBody: string;
  status: EmailTemplateStatus | string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateEmailTemplatePayload {
  name: string;
  subject: string;
  htmlBody: string;
}

export interface UpdateEmailTemplatePayload {
  subject: string;
  htmlBody: string;
  status?: EmailTemplateStatus;
}

export async function listEmailTemplates(): Promise<EmailTemplate[]> {
  const response = await signedFetch(`${PREFIX}/email/templates`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to list email templates');
  }
  return Array.isArray(data?.templates) ? (data.templates as EmailTemplate[]) : [];
}

export async function createEmailTemplate(
  payload: CreateEmailTemplatePayload,
): Promise<EmailTemplate> {
  const response = await signedFetch(`${PREFIX}/email/templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to create email template');
  }
  return data as EmailTemplate;
}

export async function updateEmailTemplate(
  id: number,
  payload: UpdateEmailTemplatePayload,
): Promise<EmailTemplate> {
  const response = await signedFetch(`${PREFIX}/email/templates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to update email template');
  }
  return data as EmailTemplate;
}

export interface EmailHistoryRecipient {
  sentId: number;
  ceaNumber: string;
  email: string | null;
  purpose: string;
  sentAt: string | null;
}

export interface EmailHistoryBatch {
  uuid: string;
  sentAt: string | null;
  recipientCount: number;
  recipients: EmailHistoryRecipient[];
}

/**
 * Returns the send-history batches for the given email template. The backend
 * route is wired up once the email-campaign table exists; until then it may
 * legitimately return an empty list (or 404, which we soft-fail to []).
 */
export async function getEmailTemplateHistory(
  id: number,
): Promise<EmailHistoryBatch[]> {
  const response = await signedFetch(`${PREFIX}/email/templates/${id}/history`);
  if (response.status === 404) return [];
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to load template history');
  }
  return Array.isArray(data?.batches) ? (data.batches as EmailHistoryBatch[]) : [];
}

export interface AiEditEmailTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface AiEditEmailResult {
  html: string;
  message: string;
}

/**
 * Ask the backend to call GPT and rewrite the HTML body based on a user
 * prompt. The endpoint is stateless — pass the full chat history each turn.
 */
export async function aiEditEmailTemplate(payload: {
  prompt: string;
  currentHtml: string;
  history?: AiEditEmailTurn[];
}): Promise<AiEditEmailResult> {
  const response = await signedFetch(`${PREFIX}/email/templates/ai-edit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to ask the AI editor');
  }
  return data as AiEditEmailResult;
}

export interface SendEmailRecipient {
  email: string;
  ceaNumber?: string;
  /** Ordered values for {{1}}, {{2}}, … placeholders. */
  variables?: string[];
}

export interface SendEmailTemplateResponse {
  ok: true;
  id: number;
  recipientCount: number;
  broadcastScheduled: boolean;
}

export async function sendEmailTemplate(
  id: number,
  payload: {
    purpose: string;
    recipients: SendEmailRecipient[];
  },
): Promise<SendEmailTemplateResponse> {
  const response = await signedFetch(`${PREFIX}/email/templates/${id}/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Failed to send email template');
  }
  return data as SendEmailTemplateResponse;
}
